import { useEffect, useRef } from 'react';
import { ShieldCheck, ShieldX } from 'lucide-react';

export type AccessCardPhase = 'default' | 'access-granted' | 'access-denied';

interface AccessCardProps {
  cardImageB64: string | null;
  phase?: AccessCardPhase;
  reason?: string;
  onAnimationComplete?: () => void;
}

const ANIMATION_DURATION_MS = 1500;

export default function AccessCard({
  cardImageB64,
  phase = 'default',
  reason,
  onAnimationComplete,
}: AccessCardProps) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (phase === 'access-granted' && onAnimationComplete && !firedRef.current) {
      firedRef.current = true;
      timerRef.current = setTimeout(onAnimationComplete, ANIMATION_DURATION_MS);
    }
    if (phase !== 'access-granted') {
      firedRef.current = false;
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [phase, onAnimationComplete]);

  return (
    <div className="relative rounded-xl overflow-hidden shadow-2xl w-full max-w-md mx-auto">
      {/* Card image or placeholder */}
      {cardImageB64 ? (
        <img
          src={`data:image/png;base64,${cardImageB64}`}
          alt="Access card"
          className="w-full block"
        />
      ) : (
        <div className="bg-[#0f1428] w-full aspect-[856/540] flex items-center justify-center">
          <span className="text-gray-500 text-sm">Card generating…</span>
        </div>
      )}

      {/* ACCESS GRANTED overlay */}
      {phase === 'access-granted' && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/60"
          style={{ animation: `fadeInGranted ${ANIMATION_DURATION_MS}ms ease forwards` }}
        >
          <div
            className="w-6 h-6 rounded-full"
            style={{ animation: `pulseGreen ${ANIMATION_DURATION_MS}ms ease forwards` }}
          />
          <div className="flex items-center gap-2 text-green-400 font-bold text-xl tracking-widest">
            <ShieldCheck className="h-6 w-6" />
            ACCESS GRANTED
          </div>
        </div>
      )}

      {/* ACCESS DENIED overlay */}
      {phase === 'access-denied' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/70">
          <div className="w-6 h-6 rounded-full bg-red-500" />
          <div className="flex items-center gap-2 text-red-400 font-bold text-xl tracking-widest">
            <ShieldX className="h-6 w-6" />
            ACCESS DENIED
          </div>
          {reason && (
            <p className="text-red-300 text-sm text-center px-6">{reason}</p>
          )}
        </div>
      )}

      {/* CSS keyframe animations injected via a style tag */}
      <style>{`
        @keyframes pulseGreen {
          0%   { background-color: #6b7280; }
          50%  { background-color: #22c55e; box-shadow: 0 0 12px #22c55e; }
          100% { background-color: #22c55e; }
        }
        @keyframes fadeInGranted {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
